const COMPRESSION_MIN_BYTES = 1024 * 1024;
const MAX_IMAGE_DIMENSION = 2400;
const JPEG_QUALITY = 0.8;
const COMPRESSIBLE_IMAGE_TYPES = new Set(['image/jpeg', 'image/png', 'image/webp']);

export function formatBytes(bytes) {
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function makeFileId(file) {
  return `${file.name}-${file.size}-${file.lastModified}-${Math.random().toString(36).slice(2)}`;
}

function compressedFileName(name) {
  const base = name.replace(/\.[^.]+$/, '');
  return `${base || 'photo'}.jpg`;
}

async function loadImage(file) {
  const url = URL.createObjectURL(file);
  try {
    const img = new Image();
    img.src = url;
    if (img.decode) {
      await img.decode();
    } else {
      await new Promise((resolve, reject) => {
        img.onload = resolve;
        img.onerror = reject;
      });
    }
    return img;
  } finally {
    URL.revokeObjectURL(url);
  }
}

function canvasToBlob(canvas, type, quality) {
  return new Promise((resolve, reject) => {
    canvas.toBlob((blob) => {
      if (blob) resolve(blob);
      else reject(new Error('Image compression failed.'));
    }, type, quality);
  });
}

async function compressImageFile(file) {
  if (!COMPRESSIBLE_IMAGE_TYPES.has(file.type) || file.size < COMPRESSION_MIN_BYTES) {
    return null;
  }

  const img = await loadImage(file);
  const scale = Math.min(1, MAX_IMAGE_DIMENSION / Math.max(img.naturalWidth, img.naturalHeight));
  const width = Math.max(1, Math.round(img.naturalWidth * scale));
  const height = Math.max(1, Math.round(img.naturalHeight * scale));

  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('Canvas is unavailable.');

  ctx.fillStyle = '#fff';
  ctx.fillRect(0, 0, width, height);
  ctx.drawImage(img, 0, 0, width, height);

  const blob = await canvasToBlob(canvas, 'image/jpeg', JPEG_QUALITY);
  if (blob.size >= file.size * 0.95) return null;

  return new File([blob], compressedFileName(file.name), {
    type: 'image/jpeg',
    lastModified: Date.now(),
  });
}

export async function prepareUploadFile(file) {
  try {
    const compressedFile = await compressImageFile(file);
    return {
      id: makeFileId(file),
      originalFile: file,
      compressedFile,
      useOriginal: false,
      compressionError: false,
    };
  } catch (err) {
    console.warn('Image compression skipped:', err);
    return {
      id: makeFileId(file),
      originalFile: file,
      compressedFile: null,
      useOriginal: true,
      compressionError: true,
    };
  }
}

export function getUploadFileForSubmit(file) {
  return file.useOriginal || !file.compressedFile ? file.originalFile : file.compressedFile;
}
